/**
 * KAN-834: вкладка "Мои лиды": клиенты, закреплённые за вошедшим менеджером,
 * поиск по имени и телефону, свежая активность сверху.
 * KAN-851: значок теплоты, чипы фильтра по теплоте, организация ведёт в свою карточку.
 * KAN-857: Telegram-аватар клиента слева в строке.
 * KAN-897: вкладки "В работе" (по умолчанию) и "Отработано" (?tab=worked) со
 * счётчиками. Логика вкладок в useMyLeads и lib/mobileLeadsTabs.
 * KAN-900: кнопки "Отработано" в строках нет, итог ставится в карточке клиента.
 * KAN-915: вкладка "Следующая связь" (?tab=next): лиды с перезвоном в будущем.
 */
import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { MobileLeadRow } from '@/components/mobile/MobileLeadRow';
import { MobileLeadsTabs } from '@/components/mobile/MobileLeadsTabs';
import { MobileTemperatureChips } from '@/components/mobile/MobileTemperatureChips';
import { useDebounce } from '@/hooks/useDebounce';
import { useMyLeads } from '@/hooks/useMobileData';
import { mobileLeadsLevelOf, type MobileLeadsTab } from '@/lib/mobileLeadsTabs';

function emptyText(tab: MobileLeadsTab, hasAny: boolean, searched: boolean): string {
  if (hasAny) return 'С такой теплотой никого нет';
  if (searched) return 'Никого не нашли';
  if (tab === 'worked') return 'Отработанных пока нет';
  if (tab === 'next') return 'Запланированных связей нет';
  return 'За вами пока нет клиентов';
}

export function MobileLeadsPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search.trim(), 300);
  const { tab, setTab, counts, all, leads, temperature, total, isLoading, isError, hasManager } =
    useMyLeads(debounced);
  const levelOf = mobileLeadsLevelOf(tab);

  return (
    <section className="px-4 py-4" data-testid="mobile-leads-page">
      <h1 className="mb-3 text-xl font-semibold text-gray-900">Мои лиды</h1>
      {hasManager && <MobileLeadsTabs value={tab} counts={counts} onChange={setTab} />}
      <label className="relative mb-3 block">
        <span className="sr-only">Поиск</span>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Имя или телефон"
          className="h-12 w-full rounded-xl border border-gray-300 bg-white pl-10 pr-3 text-base outline-none focus:border-blue-500"
        />
      </label>
      {hasManager && <MobileTemperatureChips value={temperature.filter} onChange={temperature.setFilter} />}
      {!hasManager && (
        <p className="py-12 text-center text-sm text-gray-500">Список доступен менеджеру со своей записью</p>
      )}
      {hasManager && isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-label="Загрузка" />
        </div>
      )}
      {isError && <p className="py-8 text-center text-sm text-red-600">Не удалось загрузить лидов</p>}
      {hasManager && !isLoading && !isError && leads.length === 0 && (
        <p className="py-12 text-center text-sm text-gray-500">{emptyText(tab, all.length > 0, !!debounced)}</p>
      )}
      <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
        {leads.map((client) => (
          <MobileLeadRow key={client.id} client={client} level={levelOf(client)} tab={tab} />
        ))}
      </ul>
      {total > all.length && (
        <p className="mt-2 text-center text-xs text-gray-500">
          Показаны {all.length} из {total}, уточните поиск
        </p>
      )}
    </section>
  );
}
